import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import publicAPI from '../../publicApi';
import AppPrompt from '../../components/AppPrompt';
import './PublicPage.css';

/**
 * 공개 수업일지 페이지
 * URL: /:tenantId/lessons/:id?token=xxx
 */
export default function PublicLessonView() {
  const { tenantId, id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLesson = useCallback(async () => {
    if (!token) {
      setError('유효하지 않은 링크입니다');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data } = await publicAPI.getLesson(tenantId, id, token);
      setLesson(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load lesson:', err);
      setError(err.response?.data?.error || '수업일지를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, [tenantId, id, token]);

  useEffect(() => {
    loadLesson();
  }, [loadLesson]);

  if (loading) {
    return (
      <div className="public-page">
        <div className="public-container">
          <div className="loading-spinner">로딩 중...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-page">
        <div className="public-container">
          <div className="error-message">
            <h3>⚠️ 오류</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="public-page">
        <div className="public-container">
          <div className="error-message">
            <h3>📋 수업일지를 찾을 수 없습니다</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <AppPrompt />
      <div className="public-container">
        {/* 헤더 */}
        <div className="public-header">
          <div className="header-badge">수업일지</div>
          <h1 className="public-title">{lesson.title}</h1>
          <div className="public-meta">
            <span className="meta-item">
              📅 {format(new Date(lesson.date), 'yyyy년 MM월 dd일')}
            </span>
            {lesson.classGroup?.name && (
              <span className="meta-item">
                👥 {lesson.classGroup.name}
              </span>
            )}
            {lesson.staff?.name && (
              <span className="meta-item">
                👨‍🏫 {lesson.staff.name}
              </span>
            )}
          </div>
        </div>

        {/* 수업 내용 */}
        {lesson.content && (
          <div className="public-section">
            <h2 className="section-title">📚 수업 내용</h2>
            <div className="section-content">{lesson.content}</div>
          </div>
        )}

        {/* 과제 */}
        {lesson.homework && (
          <div className="public-section">
            <h2 className="section-title">📝 과제</h2>
            <div className="section-content">{lesson.homework}</div>
          </div>
        )}

        {/* 시험 결과 */}
        {lesson.tests && lesson.tests.length > 0 && (
          <div className="public-section">
            <h2 className="section-title">📊 시험 결과</h2>
            <div className="test-results">
              {lesson.tests.map((test, index) => (
                <div key={index} className="test-result-card">
                  <div className="test-name">{test.testName}</div>
                  {test.score !== undefined && (
                    <div className="test-score">{test.score}점</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 개별 피드백 */}
        {lesson.studentFeedback && (
          <div className="public-section highlight">
            <h2 className="section-title">💬 선생님의 피드백</h2>
            <div className="section-content">{lesson.studentFeedback.feedback}</div>
            {lesson.studentFeedback.rating && (
              <div className="rating">
                평가: {'⭐'.repeat(lesson.studentFeedback.rating)}
              </div>
            )}
          </div>
        )}

        {/* 푸터 */}
        <div className="public-footer">
          <p className="footer-text">EduStaff 학원관리 시스템</p>
        </div>
      </div>
    </div>
  );
}
